import { Picture } from 'components/Picture';
import { AdSplash } from './splash';
import styles from './styles.scss';
import { AdTitle, AdInfo, AdButton } from './adInfo';
import cn from 'classnames';
import { AdBrand } from './brand';
import { getCreative, getSlot, MergedProps, RedAdProps } from 'components/Ad';
import { NotifyLink } from 'components/Shared/NotifyLink';
import { Ad } from 'models/Ad';
import { ReactNode, useCallback, useEffect, useState } from 'react';
import { differenceInHours } from 'date-fns';
import { useIntersectionObserver, useInterval } from 'hooks';
import { RedAd } from 'models/RedAd';
import { rm } from 'utils';
import { ThemeMeta } from 'models/SectionBlock';
import { isMobile, isMobileLayout } from 'components/Shared/responsive';
import { useSelector } from 'react-redux';
import { showCampaignCountDownTimer } from 'components/Analytics/selector';
import { TrackedElement } from 'hooks/useIntersectionObserver';
import { GtmPromotion } from 'store/googleTagManager/types';

interface AdBaseProps extends Ad, MergedProps {
  children?: ReactNode;
  position?: number;
  className?: string;
}
const AdBase = ({
  googleAnalyticsKey,
  googleAnalyticsName,
  ticket,
  url,
  className,
  position = 1,
  children,
  onAdView,
  adTemplate,
  onAdClick
}: AdBaseProps) => {
  const { setElementRef } = useIntersectionObserver({
    onVisible: handleElementVisible
  });

  function handleElementVisible(element: TrackedElement<GtmPromotion>) {
    onAdView(element.payload);
  }

  const gtmPromotion = {
    promotion_id: googleAnalyticsKey,
    promotion_name: googleAnalyticsName,
    creative_name: getCreative(adTemplate),
    creative_slot: getSlot(position)
  };

  const handleOnClick = () => {
    onAdClick(gtmPromotion);
  };

  return (
    <NotifyLink
      setRef={(node: Element) => setElementRef({ ref: node, payload: gtmPromotion })}
      onClick={handleOnClick}
      ticket={ticket}
      url={url}
      className={cn(styles.adBase, className)}
    >
      {children}
    </NotifyLink>
  );
};

const INTERVAL_DELAY = 60000;
const CampaignInterval = ({
  campaignEnd,
  className
}: Required<Pick<RedAd, 'campaignEnd'>> & { className?: string }) => {
  const [value, setValue] = useState<null | string>();
  const cb = useCallback(() => {
    const date = new Date(campaignEnd);
    const now = Date.now();
    if (showCampaignCountDownTimer(date)) {
      setValue(rm('campaign_hours_left', differenceInHours(date, now)));
    } else {
      setValue(null);
    }
  }, [campaignEnd]);
  useEffect(cb, [cb]);
  useInterval(cb, INTERVAL_DELAY);
  return <span className={cn(styles.campaignEnd, className)}>{value}</span>;
};

export const RedPanorama = ({
  ad,
  className,
  onSurface,
  ...props
}: RedAdProps & MergedProps & ThemeMeta) => {
  const { url, ticket, brand, campaignEnd, image, title, splash, splashHeader, themeSettings } = ad;
  const splashDimensions = {
    width: 210,
    height: 70
  };
  const mobileLayout = useSelector(isMobile);

  return (
    <AdBase {...ad} {...props} className={cn(styles.panorama, className)}>
      <AdInfo
        className={styles.adInfo}
        size="large"
        triangle
        onSurface={onSurface}
        background={themeSettings?.panoramaPlate}
        backgroundType={'panorama'}
        themeSplash={
          !showCampaignCountDownTimer(campaignEnd) ? themeSettings?.logotypePlate : undefined
        }
        themeSplashClassName={styles.themeSplashPanorama}
        themeSplashDimension={splashDimensions}
      >
        <div className={styles.adInfoWrapper}>
          {brand && <AdBrand {...brand} size="large" position="top" align="left" />}
          <AdTitle size="large">{title}</AdTitle>
          {!mobileLayout && <AdButton className={styles.adButton} url={url} ticket={ticket} />}
        </div>
      </AdInfo>
      <Picture className={styles.image} {...image} />
      <AdSplash
        className={styles.splash}
        size="small"
        onSurface={onSurface}
        splash={splash}
        splashHeader={splashHeader}
      />
    </AdBase>
  );
};

export const RedPanoramaImage = ({
  ad,
  className,
  onSurface,
  ...props
}: RedAdProps & MergedProps & ThemeMeta) => {
  const { image, brand } = ad;
  return (
    <AdBase {...ad} {...props} className={cn(styles.panorama, className)}>
      <Picture className={styles.image} {...image}>
        {brand && <AdBrand {...brand} size="large" align="left" position="bottom" />}
      </Picture>
    </AdBase>
  );
};

/* Full Size ad is with full image width and text in the middle */
export const RedFullSize = ({ ad, className, onSurface, ...props }: RedAdProps & MergedProps) => {
  const { image, title, url, ticket, splash, splashHeader, campaignEnd, themeSettings } = ad;
  const splashDimensions = {
    width: 210,
    height: 70
  };
  return (
    <AdBase {...ad} {...props} className={cn(styles.fullSize, className)}>
      <Picture className={styles.image} {...image}>
        <AdInfo
          className={styles.adInfo}
          transparent
          themeSplash={
            !showCampaignCountDownTimer(campaignEnd) ? themeSettings?.logotypePlate : undefined
          }
          backgroundType={'fullSize'}
          themeSplashClassName={styles.themeSplashPanorama}
          themeSplashDimension={splashDimensions}
        >
          {campaignEnd && showCampaignCountDownTimer(campaignEnd) && (
            <CampaignInterval campaignEnd={campaignEnd} className={styles.campaignMargin} />
          )}
          <AdTitle size="small" align="center" className={styles.textShadow}>
            {title}
          </AdTitle>
          <AdButton className={styles.adButton} url={url} ticket={ticket} />
        </AdInfo>
      </Picture>
      <AdSplash
        className={styles.splash}
        size="small"
        onSurface={onSurface}
        splash={splash}
        splashHeader={splashHeader}
      />
    </AdBase>
  );
};

/* Half size ad is with full image width and text to right  */
export const RedHalfSize = ({ ad, className, onSurface, ...props }: RedAdProps & MergedProps) => {
  const { image, title, url, ticket, splash, brand, splashHeader, campaignEnd, themeSettings } = ad;
  const splashDimensions = {
    width: 210,
    height: 70
  };
  const mobileLayout = useSelector(isMobileLayout);
  return (
    <AdBase {...ad} {...props} className={cn(styles.halfSize, className)}>
      <Picture wrapperClassName={styles.wrapper} className={styles.image} {...image}>
        {brand && (
          <AdBrand {...brand} size="large" align={mobileLayout ? 'left' : 'right'} position="top" />
        )}
        <AdInfo
          className={styles.adInfo}
          transparent
          themeSplash={
            !showCampaignCountDownTimer(campaignEnd) ? themeSettings?.logotypePlate : undefined
          }
          backgroundType={'halfSize'}
          themeSplashClassName={styles.themeSplashPanorama}
          themeSplashDimension={splashDimensions}
        >
          <AdTitle size="small" className={styles.textShadow}>
            {title}
          </AdTitle>
          <AdButton className={styles.adButton} url={url} ticket={ticket} />
        </AdInfo>
      </Picture>
      <AdSplash
        className={styles.splash}
        onSurface={onSurface}
        size="small"
        splash={splash}
        splashHeader={splashHeader}
      />
    </AdBase>
  );
};

/* Quarter size ad is with half image, half red info  */
export const RedQuarter = ({ ad, className, onSurface, ...props }: RedAdProps & MergedProps) => {
  const { campaignEnd, image, title, url, ticket, brand, splash, splashHeader, themeSettings } = ad;
  const mobileLayout = useSelector(isMobileLayout);
  const plate = mobileLayout
    ? themeSettings?.quarterMobilePlate
    : themeSettings?.quarterDesktopPlate;
  const dimensions = mobileLayout
    ? {
        width: 488,
        height: 123
      }
    : {
        width: 380,
        height: 250
      };

  const splashDimensions = {
    width: 140,
    height: 46
  };

  const centerContent = !showCampaignCountDownTimer(campaignEnd);

  return (
    <AdBase {...ad} {...props} className={cn(styles.quarter, className)}>
      <Picture wrapperClassName={styles.image} {...image}>
        {brand && (
          <AdBrand {...brand} size="large" align={mobileLayout ? 'left' : 'right'} position="top" />
        )}
        <AdInfo
          className={styles.adInfo}
          onSurface={onSurface}
          background={plate}
          backgroundType={'quarter'}
          backgroundDimension={dimensions}
          transparent={!mobileLayout}
          themeSplash={
            !showCampaignCountDownTimer(campaignEnd) ? themeSettings?.logotypePlate : undefined
          }
          themeSplashDimension={splashDimensions}
          themeSplashClassName={styles.themeSplashQuarter}
        >
          <div
            className={cn(styles.adInfoWrapper, {
              [styles.redAdInfoWrapper]: centerContent
            })}
          >
            {campaignEnd && <CampaignInterval campaignEnd={campaignEnd} />}
            <AdTitle size="small">{title}</AdTitle>
            <AdButton className={styles.adButton} url={url} ticket={ticket} />
          </div>
        </AdInfo>
      </Picture>
      <AdSplash
        onSurface={onSurface}
        className={styles.splash}
        size="small"
        splash={splash}
        splashHeader={splashHeader}
      />
    </AdBase>
  );
};

export const CurrentCampaign = ({ ad, className, ...props }: RedAdProps & MergedProps) => {
  const { title, splash, splashHeader } = ad;

  return (
    <AdBase {...ad} {...props} className={cn(styles.currentCampaign, className)}>
      <AdInfo className={styles.adInfo}>
        <AdTitle className={styles.title}>{title}</AdTitle>
        <div className={styles.splash}>
          <div className={styles.splashContent}>
            {splashHeader && <span className={styles.splashHeader}>{splashHeader}</span>}
            <span>{splash}</span>
          </div>
        </div>
      </AdInfo>
    </AdBase>
  );
};

export const RedLeaderboard = ({ ad, ...props }: RedAdProps & MergedProps) => {
  const { image } = ad;

  return (
    <AdBase {...ad} {...props}>
      <Picture {...image} />
    </AdBase>
  );
};
